import * as geometryEngine from '@arcgis/core/geometry/geometryEngine';
import { Col, Row } from 'antd';
import { observer } from 'mobx-react';
import { useRootStore } from 'models/rootModel';
import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { getLeaseholdTypes } from 'utils/api';
import { formatMobilePhoneNumber, getCaseStatusName } from 'utils/caseHelper';
import { getDateWithoutTime, getTimeWithoutDate } from 'utils/dateTime';
import { CaseStatus, CaseType, DomainName, Panel, Step } from 'utils/enumerations';
import { PreviewBlock } from './PreviewBlock';
import { Selectable } from './Selectable';

export const PaddingBlock = styled.div`
  padding-bottom: 0.5cm;
`;

export const PreviewSubHeader = styled(Col)`
  font-weight: regular;
  font-size: 10px;
  color: #454954;
`;

export const PreviewValue = styled(Col)`
  font-weight: regular;
  font-size: 12px;
  color: #23IF20;
`;

export const PreviewLineThroughValue = styled(Col)`
  font-weight: regular;
  font-size: 12px;
  color: #23IF20;
  text-decoration: line-through;
`;
export const PreviewFormattedValue = styled(Col)`
  font-weight: regular;
  font-size: 12px;
  color: #23IF20;
  white-space: pre-wrap;
`;

const RegulationPointDomainName = 'DP_TAREGULATION_POINT';
const RegulationPolylineDomainName = 'DP_TAREGULATION_POLYLINE';
const RegulationPolygonDomainName = 'DP_TAREGULATION_POLYGON';

function groupBy(list, keyGetter, geometryEngine, unit) {
  const map = new Map();
  list.forEach(item => {
    const key = keyGetter(item);
    const collection = map.get(key);
    if (!collection) {
      let dimension = '';
      if (unit === 'meters') {
        dimension = geometryEngine?.planarLength(item.polyline, unit);
      } else if (unit === 'square-meters') {
        dimension = geometryEngine?.planarArea(item.polygon, unit);
      }
      map.set(key, [{ ...item, count: 1, dimension }]);
    } else {
      if (unit === 'meters') {
        collection[0].dimension += geometryEngine?.planarLength(item.polyline, unit);
      } else if (unit === 'square-meters') {
        collection[0].dimension += geometryEngine?.planarArea(item.polygon, unit);
      }
      collection[0].count = ++collection[0].count;
    }
  });
  return map;
}

const SelectedItem = ({ itemKey, itemValue, lineThrough, formatted = false }) => {
  return (
    <Row key={`blockKey#${itemKey}`}>
      {lineThrough ? (
        <PreviewLineThroughValue>{<Selectable itemKey={itemKey} itemValue={itemValue} />}</PreviewLineThroughValue>
      ) : formatted ? (
        <PreviewFormattedValue>{<Selectable itemKey={itemKey} itemValue={itemValue} />}</PreviewFormattedValue>
      ) : (
        <PreviewValue>{<Selectable itemKey={itemKey} itemValue={itemValue} />}</PreviewValue>
      )}
    </Row>
  );
};

const MultiSelectableItem = ({ values }) => {
  return values.key.map(nestedKey => {
    const keyIndex = values.key.findIndex(valkey => valkey === nestedKey);
    const value = values.value[keyIndex];
    return (
      <Fragment key={value}>
        <Selectable itemKey={nestedKey} itemValue={value} />
        <span> </span>
      </Fragment>
    );
  });
};

export const PreviewBlockSection = ({
  showLabel,
  label,
  values,
  span = 12,
  offset = 0,
  lineThrough = false,
  formatted = false,
  spaceAbove = false,
  dataTestId
}) => {
  return (
    <Col span={span} offset={offset} data-testid={dataTestId}>
      {spaceAbove ? <PaddingBlock /> : null}
      <Row justify="start">
        <PreviewSubHeader>{showLabel ? <span>{label}:</span> : ''}</PreviewSubHeader>
      </Row>
      {values?.map((val, index) => {
        if (Array.isArray(val.key)) {
          return lineThrough ? (
            <PreviewLineThroughValue key={val.key + index}>
              {<MultiSelectableItem values={val} />}
            </PreviewLineThroughValue>
          ) : (
            <PreviewValue key={val + index}>{<MultiSelectableItem values={val} />}</PreviewValue>
          );
        } else {
          return (
            <SelectedItem
              key={val + index}
              lineThrough={lineThrough}
              itemKey={val.key}
              itemValue={val.value}
              formatted={formatted}
            />
          );
        }
      })}
    </Col>
  );
};

export const getPreviewPostAddress = (zipCode, city) => {
  let postalAddress = '';
  if (zipCode) {
    if (zipCode.length >= 5) {
      postalAddress = zipCode.substr(0, 3) + ' ' + zipCode.substr(3, 2);
    } else {
      postalAddress = zipCode;
    }
  }
  if (city) {
    postalAddress += ' ' + city;
  }
  return postalAddress;
};

const getPreviewDateTime = (date, time) => {
  return `${date ? getDateWithoutTime(date) : ''} ${time ? getTimeWithoutDate(time) : ''}`;
};

const getPreviewConnectedCaseShortTypeName = (t, caseType) => {
  switch (caseType) {
    case CaseType.DiggingPermit:
      return t('diggingPermits:DiggingPermit');
    case CaseType.Leasehold:
      return t('diggingPermits:GenericLeasehold');
    case CaseType.TrafficArrangementPlan:
      return t('preview:taPlan');
    default:
      return null;
  }
};

export const ApplicantPreviewBlock = observer(({ applicant, billingInfo, currentPanel, showLabel = true }) => {
  const [t] = useTranslation();

  const formatSocialAndOrgNumber = value => {
    return value && value.length === 10 ? `${value.substr(0, 6)}-${value.substr(6, 4)}` : value;
  };

  return (
    <PreviewBlock
      step={Step.Basic}
      panel={Panel.Questioneer}
      title={t('preview:applicant')}
      currentPanel={currentPanel}
    >
      <PreviewBlockSection
        label={t('preview:applicant')}
        showLabel={showLabel}
        values={[
          {
            key: 'organisationName',
            value: applicant.organisationName ? applicant.organisationName : ''
          },
          {
            key: 'organisationNumber',
            value: applicant.organisationNumber ? formatSocialAndOrgNumber(applicant.organisationNumber) : ''
          },
          { key: 'billingAddress', value: billingInfo.billingAddress },
          {
            key: ['billingPostalCode', 'billingPostalCity'],
            value: [getPreviewPostAddress(billingInfo.billingPostalCode), billingInfo.billingPostalCity]
          }
        ]}
      />
      <Col span={12}>
        <Row gutter={[0, 12]}>
          <PreviewBlockSection
            label={t('preview:contactPerson')}
            showLabel={showLabel}
            values={[{ key: 'applicantName', value: applicant.applicantName }]}
          />
          <PreviewBlockSection
            label={t('preview:email')}
            showLabel={showLabel}
            values={[{ key: 'email', value: applicant.email }]}
          />
          <PreviewBlockSection
            label={t('preview:mobilePhoneNumber')}
            showLabel={showLabel}
            values={[{ key: 'mobilePhoneNumber', value: formatMobilePhoneNumber(applicant.mobilePhoneNumber) }]}
          />
          {applicant.phoneNumber && (
            <PreviewBlockSection
              label={t('preview:phone')}
              showLabel={showLabel}
              values={[{ key: 'phone', value: applicant.phoneNumber }]}
            />
          )}
        </Row>
      </Col>
    </PreviewBlock>
  );
});

export const MapLegendBlock = observer(({ taRegulationPoints, taRegulationPolylines, taRegulationPolygons }) => {
  const rootStore = useRootStore();
  const [t] = useTranslation();
  const taRegulationPointsGroup = groupBy(taRegulationPoints, ta => ta.objectType + ta.comment);
  const taRegulationPolylinesGroup = groupBy(
    taRegulationPolylines,
    ta => ta.objectType + ta.comment,
    geometryEngine,
    'meters'
  );
  const taRegulationPolygonsGroup = groupBy(
    taRegulationPolygons,
    ta => ta.objectType + ta.comment,
    geometryEngine,
    'square-meters'
  );
  return (
    <>
      <Row>
        <Col span={4}>{t('preview:index')}</Col>
        <Col span={4}>{t('preview:name')}</Col>
        <Col span={8}>{t('preview:comment')}</Col>
        <Col span={4}>{t('preview:count')}</Col>
        <Col span={4}>{t('preview:dimensions')}</Col>
      </Row>
      {Array.from(taRegulationPointsGroup.values()).map(values =>
        values.map(taRegulationPoint => {
          return (
            <Row key={taRegulationPoint.gid}>
              <Col span={4}>{taRegulationPoint.snapshotIndex}</Col>
              <Col span={4}>
                {
                  rootStore.taDomains
                    .find(x => x.domainName === RegulationPointDomainName)
                    ?.codedValues.find(c => c.code + '' === taRegulationPoint.objectType + '')?.name
                }
              </Col>
              <Col span={8}>{taRegulationPoint.comment}</Col>
              <Col span={4}>{taRegulationPoint.count}</Col>
              <Col span={4}></Col>
            </Row>
          );
        })
      )}
      {Array.from(taRegulationPolylinesGroup.values()).map(values =>
        values.map(taRegulationPolyline => {
          return (
            <Row key={taRegulationPolyline.gid}>
              <Col span={4}>{taRegulationPolyline.snapshotIndex}</Col>
              <Col span={4}>
                {
                  rootStore.taDomains
                    .find(x => x.domainName === RegulationPolylineDomainName)
                    ?.codedValues.find(c => c.code + '' === taRegulationPolyline.objectType + '')?.name
                }
              </Col>
              <Col span={8}>{taRegulationPolyline.comment}</Col>
              <Col span={4}>{taRegulationPolyline.count}</Col>
              <Col span={4}>{taRegulationPolyline.dimension?.toFixed(2)} m</Col>
            </Row>
          );
        })
      )}
      {Array.from(taRegulationPolygonsGroup.values()).map(values =>
        values.map(taRegulationPolygon => {
          return (
            <Row key={taRegulationPolygon.gid}>
              <Col span={4}>{taRegulationPolygon.snapshotIndex}</Col>
              <Col span={4}>
                {
                  rootStore.taDomains
                    .find(x => x.domainName === RegulationPolygonDomainName)
                    ?.codedValues.find(c => c.code + '' === taRegulationPolygon.objectType + '')?.name
                }
              </Col>
              <Col span={8}>{taRegulationPolygon.comment}</Col>
              <Col span={4}>{taRegulationPolygon.count}</Col>
              <Col span={4}>
                {taRegulationPolygon.dimension?.toFixed(2)} m<sup>2</sup>
              </Col>
            </Row>
          );
        })
      )}
    </>
  );
});

export const BillingPreviewBlock = observer(({ billingInfo, applicant, currentPanel, showLabel = true }) => {
  const [t] = useTranslation();
  return (
    <PreviewBlock
      step={Step.Basic}
      panel={Panel.InvoiceAddress}
      title={t('preview:invoice')}
      currentPanel={currentPanel}
    >
      <PreviewBlockSection
        label={t('preview:sendInvoice')}
        showLabel={showLabel}
        values={[
          {
            key: 'organisationName',
            value: applicant.organisationName
          },
          { key: 'alternativeBillingAddress', value: billingInfo.alternativeBillingAddress },
          {
            key: ['alternativeBillingPostalCode', 'alternativeBillingPostalCity'],
            value: [
              getPreviewPostAddress(billingInfo.alternativeBillingPostalCode),
              billingInfo.alternativeBillingPostalCity
            ]
          }
        ]}
      />

      <PreviewBlockSection
        label={t('preview:invoiceMarking')}
        showLabel={showLabel}
        values={[{ key: 'invoiceMarking', value: billingInfo.invoiceMarking }]}
      />
    </PreviewBlock>
  );
});

export const CaseInfoPreviewBlock = observer(
  ({ caseInfo, caseStatus, currentPanel, prolongedStatus, showLabel = true }) => {
    const [t] = useTranslation();
    return (
      <PreviewBlock
        step={Step.Basic}
        panel={Panel.Case}
        title={t('preview:caseInformation')}
        currentPanel={currentPanel}
      >
        <PreviewBlockSection
          label={t('preview:caseName')}
          showLabel={showLabel}
          values={[{ key: 'caseName', value: caseInfo.caseName }]}
        />
        <PreviewBlockSection
          span={6}
          label={t('preview:ledningskollenId')}
          showLabel={showLabel}
          values={[{ key: 'ledningskollenId', value: caseInfo.ledningskollenId }]}
        />
        <Col>
          <Row justify="start">
            <PreviewSubHeader>{t('preview:status')}:</PreviewSubHeader>
          </Row>
          <Row>
            <PreviewValue>
              <span data-testid={'previewCaseStatus'}>{getCaseStatusName(t, caseStatus)}</span>
            </PreviewValue>
          </Row>
        </Col>
        <PreviewBlockSection
          spaceAbove={true}
          span={18}
          label={t('preview:location')}
          showLabel={showLabel}
          values={[{ key: 'location', value: caseInfo.location }]}
        />
        {caseStatus === CaseStatus.Approved && prolongedStatus !== null && typeof prolongedStatus !== 'undefined' ? (
          <Col>
            <PaddingBlock />
            <Row justify="start">
              <PreviewSubHeader>{t('preview:prolongStatus')}:</PreviewSubHeader>
            </Row>
            <Row>
              <PreviewValue>
                <span data-testid={'previewProlongStatus'}>{getCaseStatusName(t, prolongedStatus)}</span>
              </PreviewValue>
            </Row>
          </Col>
        ) : null}
      </PreviewBlock>
    );
  }
);

export const TaPlanInfoPreviewBlock = observer(
  ({ caseInfo, caseStatus, currentPanel, prolongedStatus, showLabel = true }) => {
    const [t] = useTranslation();
    return (
      <PreviewBlock
        step={Step.Basic}
        panel={Panel.CreateTAPlan}
        title={t('preview:caseInformation')}
        currentPanel={currentPanel}
      >
        <PreviewBlockSection
          label={t('preview:caseName')}
          showLabel={showLabel}
          values={[{ key: 'caseName', value: caseInfo.caseName }]}
        />
        <Col>
          <Row justify="start">
            <PreviewSubHeader>{t('preview:status')}:</PreviewSubHeader>
          </Row>
          <Row>
            <PreviewValue>
              <span data-testid={'previewCaseStatus'}>{getCaseStatusName(t, caseStatus)}</span>
            </PreviewValue>
          </Row>
        </Col>
        <PreviewBlockSection
          spaceAbove={true}
          span={18}
          label={t('preview:location')}
          showLabel={showLabel}
          values={[{ key: 'location', value: caseInfo.location }]}
        />
        {caseStatus === CaseStatus.Approved && prolongedStatus !== null && typeof prolongedStatus !== 'undefined' ? (
          <Col>
            <PaddingBlock />
            <Row justify="start">
              <PreviewSubHeader>{t('preview:prolongStatus')}:</PreviewSubHeader>
            </Row>
            <Row>
              <PreviewValue>
                <span data-testid={'previewProlongStatus'}>{getCaseStatusName(t, prolongedStatus)}</span>
              </PreviewValue>
            </Row>
          </Col>
        ) : null}
      </PreviewBlock>
    );
  }
);

export const LeaseholdInfoPreviewBlock = observer(
  ({ caseStatus, currentPanel, purpose, policeCaseNumber, location, showLabel = true }) => {
    const [t] = useTranslation();
    return (
      <PreviewBlock
        step={Step.Basic}
        panel={Panel.CreateLeasehold}
        title={t('preview:caseInformation')}
        currentPanel={currentPanel}
      >
        <PreviewBlockSection
          label={t('preview:purpose')}
          showLabel={showLabel}
          values={[{ key: 'purpose', value: purpose }]}
        />
        <PreviewBlockSection
          span={6}
          label={t('preview:policeCaseNumber')}
          showLabel={showLabel}
          values={[{ key: 'policeCaseNumber', value: policeCaseNumber }]}
        />
        <Col>
          <Row justify="start">
            <PreviewSubHeader>{t('preview:status')}:</PreviewSubHeader>
          </Row>
          <Row>
            <PreviewValue>
              <span data-testid={'previewCaseStatus'}>{getCaseStatusName(t, caseStatus)}</span>
            </PreviewValue>
          </Row>
        </Col>
        <PreviewBlockSection
          spaceAbove={true}
          span={18}
          label={t('preview:location')}
          showLabel={showLabel}
          values={[{ key: 'location', value: location }]}
        />
      </PreviewBlock>
    );
  }
);

export const LeaseholdTypePreviewBlock = observer(({ type, leaseholdType, currentPanel, showLabel = true }) => {
  const [t] = useTranslation();
  const [vtLeaseholdTypes, setVtLeaseholdTypes] = useState([]);

  useEffect(() => {
    let isLoaded = true;
    const loadDomains = async () => {
      const myTypes = await getLeaseholdTypes();
      if (isLoaded) {
        setVtLeaseholdTypes(myTypes.filter(x => x.inactive === false));
      }
    };

    loadDomains();
    return () => {
      isLoaded = false;
    };
  }, []);

  const getLeaseholdTypeName = leaseholdTypeCode => {
    if (leaseholdTypeCode === undefined) {
      return '';
    }
    const myType = vtLeaseholdTypes.find(typeCode => typeCode.code === leaseholdTypeCode);
    return myType ? myType.name : '';
  };

  return (
    <PreviewBlock
      step={Step.Basic}
      panel={Panel.LeaseholdTypes}
      title={t('preview:leaseholdTypes')}
      currentPanel={currentPanel}
    >
      <PreviewBlockSection
        label={t('preview:typeAndUse')}
        showLabel={showLabel}
        values={[{ key: 'type', value: type }]}
      />
      <PreviewBlockSection
        span={6}
        label={t('preview:leaseholdType')}
        showLabel={showLabel}
        values={[{ key: 'leaseholdtype', value: getLeaseholdTypeName(leaseholdType) }]}
      />
    </PreviewBlock>
  );
});

export const WorkTypePreviewBlock = observer(({ workType, currentPanel, showLabel = false }) => {
  const [t] = useTranslation();
  return (
    <PreviewBlock step={Step.Basic} panel={Panel.WorkType} title={t('preview:workType')} currentPanel={currentPanel}>
      <PreviewBlockSection
        label={t('preview:workType')}
        showLabel={showLabel}
        values={[{ key: 'workType', value: workType }]}
      />
    </PreviewBlock>
  );
});

export const ManagersPreviewBlock = observer(
  ({ managers, currentPanel, locationSpecific = true, showLabel = true }) => {
    const [t] = useTranslation();

    return (
      <PreviewBlock
        step={locationSpecific ? Step.Managers : Step.Map}
        panel={Panel.InspectionManager}
        title={t('preview:managers')}
        currentPanel={currentPanel}
      >
        <Col span={24}>
          <Row gutter={[0, 12]}>
            <PreviewBlockSection
              label={t('preview:markingManager')}
              showLabel={showLabel}
              values={[{ key: 'markingManager', value: managers.markingManager }]}
            />
            <PreviewBlockSection
              span={6}
              label={t('preview:inspectionManager')}
              showLabel={showLabel}
              values={[{ key: 'inspectionManager', value: managers.inspectionManager }]}
            />
            <PreviewBlockSection
              label={t('preview:mobilePhoneNumber')}
              showLabel={showLabel}
              values={[
                {
                  key: 'markingManagerMobilePhoneNumber',
                  value: formatMobilePhoneNumber(managers.markingManagerMobilePhoneNumber)
                }
              ]}
            />
            <PreviewBlockSection
              span={6}
              label={t('preview:mobilePhoneNumber')}
              showLabel={showLabel}
              values={[
                {
                  key: 'inspectionManagerMobilePhoneNumber',
                  value: formatMobilePhoneNumber(managers.inspectionManagerMobilePhoneNumber)
                }
              ]}
            />
          </Row>
        </Col>
      </PreviewBlock>
    );
  }
);

export const ComplementingFactsPreviewBlock = observer(
  ({
    additionalInformation,
    childCases,
    parentCases,
    attachments,
    currentPanel,
    locationSpecific = true,
    showLabel = true
  }) => {
    const [t] = useTranslation();
    return (
      <PreviewBlock
        step={locationSpecific ? Step.Finish : Step.Managers}
        panel={Panel.AdditionalInformation}
        title={t('preview:complementingFacts')}
        currentPanel={currentPanel}
      >
        <PreviewBlockSection
          span={24}
          label={t('preview:complementingFacts')}
          showLabel={showLabel}
          values={[{ key: 'additionalInformation', value: additionalInformation }]}
          formatted={true}
        />

        <Col span={24}>
          <PaddingBlock />
          <Row justify="start">
            <PreviewSubHeader span={12}>{t('preview:relatedCases')}:</PreviewSubHeader> {/* StyledCol */}
            <PreviewSubHeader span={12}>{t('preview:caseOwner')}</PreviewSubHeader>
          </Row>
          {childCases?.map(childCase => (
            <Row key={childCase.referenceApplicationGID}>
              <PreviewValue span={12}>
                {childCase.purpose !== undefined
                  ? `${getPreviewConnectedCaseShortTypeName(t, childCase.childEntityType)} ${childCase.caseNumber} ${
                      childCase.purpose
                    }`
                  : `${getPreviewConnectedCaseShortTypeName(t, childCase.childEntityType)} ${childCase.caseNumber}`}
              </PreviewValue>
              <PreviewValue span={12}>{`${childCase.owner}`}</PreviewValue>
            </Row>
          ))}
          {parentCases?.map(parentCase => (
            <Row key={parentCase.referenceApplicationGID}>
              <PreviewValue span={12}>
                {parentCase.purpose !== undefined
                  ? `${getPreviewConnectedCaseShortTypeName(t, parentCase.parentEntityType)} ${parentCase.caseNumber} ${
                      parentCase.purpose
                    }`
                  : `${getPreviewConnectedCaseShortTypeName(t, parentCase.parentEntityType)} ${parentCase.caseNumber}`}
              </PreviewValue>
              <PreviewValue span={12}>{`${parentCase.owner}`}</PreviewValue>
            </Row>
          ))}
        </Col>

        <PreviewBlockSection
          spaceAbove={true}
          label={t('preview:attachedFiles')}
          showLabel={showLabel}
          span={24}
          values={[
            {
              key: 'attachments',
              value: attachments.map(a => a.fileName).join(', ')
            }
          ]}
        />
      </PreviewBlock>
    );
  }
);

export const DateAndTimePreviewBlock = observer(
  ({ dateTimeInfo, prolongedTo, currentPanel, showLabel = true, prolongedStatus }) => {
    const [t] = useTranslation();
    return (
      <PreviewBlock
        step={Step.Basic}
        panel={Panel.DateAndTimePeriod}
        title={t('preview:dateAndTimePeriod')}
        currentPanel={currentPanel}
      >
        <PreviewBlockSection
          dataTestId="previewStartDate"
          label={t('preview:startDate')}
          showLabel={showLabel}
          values={[
            {
              key: 'dateFrom',
              value: getPreviewDateTime(dateTimeInfo.dateFrom, dateTimeInfo.startTime)
            }
          ]}
        />
        <PreviewBlockSection
          span={6}
          dataTestId="previewEndDate"
          label={t('preview:endDate')}
          showLabel={showLabel}
          lineThrough={prolongedTo && prolongedStatus === CaseStatus.Approved}
          values={[{ key: 'dateTo', value: getPreviewDateTime(dateTimeInfo.dateTo, dateTimeInfo.endTime) }]}
        />
        {prolongedTo && prolongedStatus === CaseStatus.Approved ? (
          <PreviewBlockSection
            span={6}
            dataTestId="previewProlongedTo"
            label={t('preview:prolongedTo')}
            showLabel={showLabel}
            values={[
              { key: 'prolongedTo', value: `${getDateWithoutTime(prolongedTo)} ${getTimeWithoutDate(prolongedTo)}` }
            ]}
          />
        ) : null}
      </PreviewBlock>
    );
  }
);

export const EntrepreneurPreviewBlock = observer(({ entrepreneur, currentPanel, showLabel = true }) => {
  const [t] = useTranslation();
  return (
    <PreviewBlock
      step={Step.Basic}
      panel={Panel.EntrepreneurAndSupervisor}
      title={t('preview:entrepreneurAndSupervisor')}
      currentPanel={currentPanel}
    >
      <PreviewBlockSection
        label={t('preview:entrepreneur')}
        showLabel={showLabel}
        values={[
          {
            key: 'entrepreneurName',
            value: entrepreneur.entrepreneurName
          },
          {
            key: 'entrepreneurOrganisationNumber',
            value:
              entrepreneur.entrepreneurOrganisationNumber && entrepreneur.entrepreneurOrganisationNumber.length === 10
                ? `${entrepreneur.entrepreneurOrganisationNumber.substr(
                    0,
                    6
                  )}-${entrepreneur.entrepreneurOrganisationNumber.substr(6, 4)}`
                : entrepreneur.entrepreneurOrganisationNumber
          },

          { key: 'entrepreneurAddress', value: entrepreneur.entrepreneurAddress },
          {
            key: ['entrepreneurPostalCode', 'entrepreneurPostalCity'],
            value: [getPreviewPostAddress(entrepreneur.entrepreneurPostalCode), entrepreneur.entrepreneurPostalCity]
          }
        ]}
      />

      <PreviewBlockSection
        span={6}
        label={t('preview:supervisor')}
        showLabel={showLabel}
        values={[
          { key: 'supervisorName', value: entrepreneur.supervisorName },
          { key: 'supervisorPhoneNumber', value: entrepreneur.supervisorPhoneNumber },
          {
            key: 'supervisorMobilePhoneNumber',
            value: formatMobilePhoneNumber(entrepreneur.supervisorMobilePhoneNumber)
          },
          { key: 'supervisorEmail', value: entrepreneur.supervisorEmail }
        ]}
      />
    </PreviewBlock>
  );
});

export const InspectionPreviewBlock = observer(({ inspectionDate, location, showLabel = true }) => {
  const [t] = useTranslation();
  return inspectionDate === undefined && location === undefined ? null : (
    <PreviewBlock step={Step.Basic} panel={Panel.Case} title={t('preview:inspection')}>
      <PreviewBlockSection
        label={t('preview:inspectionDate')}
        showLabel={showLabel}
        values={[{ key: 'inspectionDate', value: getDateWithoutTime(inspectionDate) }]}
      />
      <PreviewBlockSection
        label={t('preview:location')}
        showLabel={showLabel}
        values={[{ key: 'location', value: location }]}
      />
    </PreviewBlock>
  );
});

export const InvoicePreviewBlock = observer(({ recoveryInvoiceDate, sendRecoveryInvoice, showLabel = true }) => {
  const [t] = useTranslation();
  return recoveryInvoiceDate === undefined && sendRecoveryInvoice === undefined ? null : (
    <PreviewBlock step={Step.Basic} panel={Panel.Case} title={t('preview:invoice')}>
      <PreviewBlockSection
        label={t('preview:sendRecoveryInvoice')}
        showLabel={showLabel}
        values={[
          {
            key: 'sendRecoveryInvoice',
            value:
              sendRecoveryInvoice === true ? t('preview:yes') : sendRecoveryInvoice === false ? t('preview:no') : ''
          }
        ]}
      />
      <PreviewBlockSection
        label={t('preview:invoiceDate')}
        showLabel={showLabel}
        values={[{ key: 'recoveryInvoiceDate', value: getDateWithoutTime(recoveryInvoiceDate) }]}
      />
    </PreviewBlock>
  );
});

export const MotivationPreviewBlock = observer(({ answer, handlingOfficerName, showLabel = true }) => {
  const [t] = useTranslation();
  return (
    <PreviewBlock step={Step.Basic} panel={Panel.Case} title={t('preview:opinion')}>
      <PreviewBlockSection
        label={t('preview:answer')}
        showLabel={showLabel}
        values={[{ key: 'answer', value: answer }]}
      />

      <PreviewBlockSection
        label={t('preview:handlingOfficer')}
        showLabel={showLabel}
        values={[{ key: 'handlingOfficerName', value: handlingOfficerName }]}
      />
    </PreviewBlock>
  );
});

export const RecoveryPreviewBlock = observer(
  ({ recoveryInspectionDate, recoveryInspectionNeeded, showLabel = true }) => {
    const [t] = useTranslation();
    return recoveryInspectionDate === undefined && recoveryInspectionNeeded === undefined ? null : (
      <PreviewBlock step={Step.Basic} panel={Panel.Case} title={t('preview:recovery')}>
        <PreviewBlockSection
          label={t('preview:inspectionNeeded')}
          showLabel={showLabel}
          values={[
            {
              key: 'recoveryInspectionNeeded',
              value:
                recoveryInspectionNeeded !== undefined && recoveryInspectionNeeded !== null
                  ? recoveryInspectionNeeded
                    ? t('preview:yes')
                    : t('preview:no')
                  : ''
            }
          ]}
        />
        <PreviewBlockSection
          label={t('preview:recoveryInspectionDate')}
          showLabel={showLabel}
          values={[{ key: 'recoveryInspectionDate', value: getDateWithoutTime(recoveryInspectionDate) }]}
        />
      </PreviewBlock>
    );
  }
);

export const PurposePreviewBlock = observer(
  ({ purpose, executedIns, domains, workMethodAndDepth, currentPanel, showLabel = true }) => {
    const [t] = useTranslation();
    const convertWorkTypes = () => {
      return domains
        ?.find(d => d.domainName === DomainName.WorkType)
        ?.codedValues.filter(domainValue => purpose.workTypes?.includes(domainValue.code))
        .map(domainValue => domainValue.description)
        .map(type => ({
          key: `workType#${type}`,
          value: type
        }));
    };
    const convertExecutedIns = () => {
      return domains
        ?.find(d => d.domainName === DomainName.WorkPerformed)
        ?.codedValues.filter(domainValue => executedIns?.includes(domainValue.code))
        .map(domainValue => domainValue.description)
        .map(executedIn => ({
          key: `executedIns#${executedIn}`,
          value: executedIn
        }));
    };
    const convertWorkMethods = () => {
      return domains
        ?.find(d => d.domainName === DomainName.WorkMethod)
        ?.codedValues.filter(domainValue => workMethodAndDepth.workMethod?.includes(domainValue.code))
        .map(domainValue => domainValue.description)
        .map(method => ({
          key: `workMethod#${method}`,
          value: method
        }));
    };
    return (
      <PreviewBlock
        step={Step.Errand}
        panel={Panel.Purpose}
        showLabel={showLabel}
        title={t('preview:purpose')}
        currentPanel={currentPanel}
      >
        <Col span={12}>
          <PreviewBlockSection
            span={22}
            label={t('preview:description')}
            showLabel={showLabel}
            values={[{ key: 'purpose', value: purpose.purpose }]}
          />
          <PaddingBlock />
          <PreviewBlockSection
            span={24}
            label={t('preview:executedIn')}
            showLabel={showLabel}
            values={convertExecutedIns()}
          />
        </Col>
        <Col span={12}>
          <PreviewBlockSection span={12} label={t('preview:area')} showLabel={showLabel} values={convertWorkTypes()} />
          <PaddingBlock />
          <Row>
            <PreviewBlockSection
              span={12}
              label={t('preview:workMethod')}
              showLabel={showLabel}
              values={convertWorkMethods()}
            />
            <PreviewBlockSection
              span={12}
              label={t('preview:depth')}
              showLabel={showLabel}
              values={[
                {
                  key: 'depth',
                  value: workMethodAndDepth.depth ? `${workMethodAndDepth.depth.toString().replace('.', ',')} m` : ''
                }
              ]}
            />
          </Row>
        </Col>
      </PreviewBlock>
    );
  }
);
